import React from 'react';
import { COURSES } from '../data/academyCourses';
import { GENERAL_CHANNELS, SEED_REPLIES, SEED_THREADS, courseChannel } from '../data/academyCommunity';
import { ROLE_LABELS, SAMPLE_ROSTER, STAFF_ACCESS_CODE, STAFF_ACCOUNTS } from '../data/academyAdmin';
import { notifyTeam } from '../lib/notify';
import { useAcademy } from './Academy';
import { courseProgress, moduleProgress } from '../utils/academyProgress';
import type { AcademyNotification, AcademyRole, ChatMessage, DirectMessage, EmailPrefKey, ForumAuthor, ForumChannel, HubState, InterestId, NotificationType, StaffSession, TeamMember } from '../types/academyHub';
import type { Course } from '../types/academy';
const STORAGE_KEY = 'cavalla.academy.hub.v1';
const STAFF_KEY = 'cavalla.academy.staff.v1';
const EMPTY: HubState = {
  threads: SEED_THREADS,
  replies: SEED_REPLIES,
  upvoted: [],
  flags: [],
  chat: [],
  team: null,
  notifications: [],
  interests: [],
  onboarded: false,
  emailPrefs: {
    enrolment: true,
    completion: true,
    team: true,
    forum: true,
    referral: true,
    nudge: true
  },
  courseOverrides: {},
  customCourses: [],
  unpublished: [],
  courseUpdates: {},
  messages: [],
  roleOverrides: {},
  announced: []
};
interface SaveCourseOptions {
  /** Announce the change to enrolled learners. */
  notify?: boolean;
  note?: string;
}
interface HubValue {
  hub: HubState;
  role: AcademyRole;
  staff: StaffSession | null;
  author: ForumAuthor;
  // catalogue
  courses: Course[];
  publishedCourses: Course[];
  findCourse: (slug: string) => Course | undefined;
  isPublished: (slug: string) => boolean;
  channels: ForumChannel[];
  // staff access
  staffSignIn: (email: string, code: string) => string | null;
  staffSignOut: () => void;
  // onboarding and settings
  setInterests: (ids: InterestId[]) => void;
  finishOnboarding: () => void;
  setEmailPref: (key: EmailPrefKey, value: boolean) => void;
  // forum
  createThread: (channel: string, title: string, body: string) => string;
  addReply: (threadId: string, body: string, parentId?: string) => void;
  toggleUpvote: (postId: string) => void;
  flagPost: (postType: 'thread' | 'reply', postId: string, reason: string) => void;
  moderate: (flagId: string, action: 'approve' | 'hide') => void;
  togglePin: (threadId: string) => void;
  hideThread: (threadId: string) => void;
  // support chat
  sendChat: (body: string) => void;
  replyAsSupport: (body: string) => void;
  leaveMessage: (email: string, body: string) => Promise<void>;
  // direct messages
  messageLearner: (learner: {
    email: string;
    name: string;
  }, body: string) => void;
  messageStaff: (body: string) => void;
  markConversationRead: (learnerEmail: string, reader: 'staff' | 'learner') => void;
  // teams
  createTeam: (name: string, organisation: string) => void;
  inviteMembers: (people: {
    name: string;
    email: string;
  }[]) => number;
  removeMember: (email: string) => void;
  assignCourses: (slugs: string[]) => void;
  // notifications
  visibleNotifications: AcademyNotification[];
  markRead: (id: string) => void;
  markAllRead: () => void;
  // course management
  saveCourse: (course: Course, options?: SaveCourseOptions) => number;
  createCourse: (course: Omit<Course, 'slug' | 'number'>) => string;
  setPublished: (slug: string, published: boolean) => void;
  setRole: (email: string, role: AcademyRole) => void;
}
const HubContext = React.createContext<HubValue | null>(null);
export function AcademyHubProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    state,
    enrol
  } = useAcademy();
  const [hub, setHub] = React.useState<HubState>(readHub);
  const [staff, setStaff] = React.useState<StaffSession | null>(readStaff);
  React.useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(hub));
    } catch {
      /* the session still works without persistence */
    }
  }, [hub]);
  React.useEffect(() => {
    try {
      if (staff) window.localStorage.setItem(STAFF_KEY, JSON.stringify(staff));else window.localStorage.removeItem(STAFF_KEY);
    } catch {
      /* ignore */
    }
  }, [staff]);

  // A staff member demoted to learner loses access straight away.
  const role: AcademyRole = staff ? hub.roleOverrides[staff.email] ?? staff.role : 'learner';
  const courses = React.useMemo(() => [...COURSES.map(c => hub.courseOverrides[c.slug] ?? c), ...hub.customCourses], [hub.courseOverrides, hub.customCourses]);
  const isPublished = React.useCallback((slug: string) => !hub.unpublished.includes(slug), [hub.unpublished]);
  const publishedCourses = React.useMemo(() => courses.filter(c => isPublished(c.slug)), [courses, isPublished]);
  const findCourse = React.useCallback((slug: string) => {
    const c = courses.find(x => x.slug === slug);
    // Drafts stay hidden from learners, but anyone already enrolled keeps access.
    if (!c) return undefined;
    return isPublished(slug) || role !== 'learner' || state.enrolments[slug] ? c : undefined;
  }, [courses, isPublished, role, state.enrolments]);
  const channels = React.useMemo(() => [...GENERAL_CHANNELS, ...publishedCourses.map(courseChannel)], [publishedCourses]);
  const notify = React.useCallback((type: NotificationType, title: string, body?: string, link?: string, extra?: Pick<AcademyNotification, 'to' | 'courseSlug'>) => {
    const item: AcademyNotification = {
      id: makeId('n'),
      type,
      title,
      body,
      link,
      read: false,
      at: new Date().toISOString(),
      ...extra
    };
    setHub(h => ({
      ...h,
      notifications: [item, ...h.notifications].slice(0, 80)
    }));
  }, []);

  // Turn learning progress into notifications. Each event key is announced once.
  React.useEffect(() => {
    const events: {
      key: string;
      type: NotificationType;
      title: string;
      body?: string;
      link?: string;
    }[] = [];
    for (const course of courses) {
      if (!state.enrolments[course.slug]) continue;
      events.push({
        key: `enrol:${course.slug}`,
        type: 'enrolment',
        title: `You are enrolled in ${course.title}`,
        body: `${course.modules.length} modules, about ${course.hours} hours.`,
        link: `/academy/courses/${course.slug}`
      });
      course.modules.forEach((mod, mi) => {
        if (moduleProgress(course, mi, state).complete) {
          events.push({
            key: `module:${course.slug}:${mi}`,
            type: 'module',
            title: `Module ${mi + 1} complete`,
            body: `${mod.title}, ${course.title}`,
            link: `/academy/courses/${course.slug}`
          });
        }
      });
      const cert = state.certificates[course.slug];
      if (cert && courseProgress(course, state).complete) {
        events.push({
          key: `cert:${course.slug}`,
          type: 'completion',
          title: 'Your certificate is ready',
          body: course.title,
          link: `/academy/certificates/${cert.id}`
        });
      }
    }
    const fresh = events.filter(e => !hub.announced.includes(e.key));
    if (!fresh.length) return;
    const at = new Date().toISOString();
    setHub(h => ({
      ...h,
      announced: [...h.announced, ...fresh.map(e => e.key)],
      notifications: [...fresh.reverse().map(e => ({
        id: makeId('n'),
        type: e.type,
        title: e.title,
        body: e.body,
        link: e.link,
        read: false,
        at
      })), ...h.notifications].slice(0, 80)
    }));
  }, [state, hub.announced, courses]);
  const author: ForumAuthor = staff ? {
    name: staff.name,
    detail: ROLE_LABELS[staff.role],
    staff: true
  } : state.learner ? {
    name: state.learner.name,
    detail: [state.learner.organisation, state.learner.country].filter(Boolean).join(', ')
  } : {
    name: 'Guest'
  };
  const staffSignIn = React.useCallback((email: string, code: string) => {
    const address = email.trim().toLowerCase();
    const override = hub.roleOverrides[address];
    // Built-in staff, or a learner a platform admin has promoted on the Users tab.
    const promoted = SAMPLE_ROSTER.find(l => l.email === address && override && override !== 'learner');
    const account: StaffSession | undefined = STAFF_ACCOUNTS.find(a => a.email === address) ?? (promoted ? {
      name: promoted.name,
      email: promoted.email,
      role: override as StaffSession['role']
    } : undefined);
    if (!account || code.trim() !== STAFF_ACCESS_CODE) return 'That email and access code do not match a staff account.';
    if (override === 'learner') return 'This account no longer has staff access. Ask a platform admin.';
    setStaff(account);
    return null;
  }, [hub.roleOverrides]);
  const staffSignOut = React.useCallback(() => setStaff(null), []);
  const setInterests = React.useCallback((ids: InterestId[]) => setHub(h => ({
    ...h,
    interests: ids
  })), []);
  const finishOnboarding = React.useCallback(() => setHub(h => ({
    ...h,
    onboarded: true
  })), []);
  const setEmailPref = React.useCallback((key: EmailPrefKey, value: boolean) => setHub(h => ({
    ...h,
    emailPrefs: {
      ...h.emailPrefs,
      [key]: value
    }
  })), []);
  const createThread = React.useCallback((channel: string, title: string, body: string) => {
    const id = makeId('t');
    setHub(h => ({
      ...h,
      threads: [{
        id,
        channel,
        title: title.trim(),
        body: body.trim(),
        author,
        createdAt: new Date().toISOString(),
        upvotes: 0,
        mine: !staff
      }, ...h.threads]
    }));
    return id;
  }, [author, staff]);
  const addReply = React.useCallback((threadId: string, body: string, parentId?: string) => {
    setHub(h => ({
      ...h,
      replies: [...h.replies, {
        id: makeId('r'),
        threadId,
        parentId,
        body: body.trim(),
        author,
        createdAt: new Date().toISOString(),
        upvotes: 0,
        mine: !staff
      }]
    }));
    // Tell the learner when staff answer their thread.
    const thread = hub.threads.find(t => t.id === threadId);
    if (staff && thread?.mine) {
      notify('forum', `${staff.name} replied to your thread`, thread.title, `/academy/community/${thread.channel}/${thread.id}`);
    }
  }, [author, staff, hub.threads, notify]);
  const toggleUpvote = React.useCallback((postId: string) => {
    setHub(h => {
      const on = h.upvoted.includes(postId);
      const delta = on ? -1 : 1;
      return {
        ...h,
        upvoted: on ? h.upvoted.filter(id => id !== postId) : [...h.upvoted, postId],
        threads: h.threads.map(t => t.id === postId ? {
          ...t,
          upvotes: t.upvotes + delta
        } : t),
        replies: h.replies.map(r => r.id === postId ? {
          ...r,
          upvotes: r.upvotes + delta
        } : r)
      };
    });
  }, []);
  const flagPost = React.useCallback((postType: 'thread' | 'reply', postId: string, reason: string) => {
    setHub(h => ({
      ...h,
      flags: [...h.flags, {
        id: makeId('f'),
        postType,
        postId,
        reason,
        status: 'pending',
        createdAt: new Date().toISOString()
      }]
    }));
  }, []);
  const moderate = React.useCallback((flagId: string, action: 'approve' | 'hide') => {
    setHub(h => {
      const flag = h.flags.find(f => f.id === flagId);
      if (!flag) return h;
      const hide = action === 'hide';
      return {
        ...h,
        flags: h.flags.map(f => f.id === flagId ? {
          ...f,
          status: hide ? 'hidden' : 'approved'
        } : f),
        threads: h.threads.map(t => hide && flag.postType === 'thread' && t.id === flag.postId ? {
          ...t,
          hidden: true
        } : t),
        replies: h.replies.map(r => hide && flag.postType === 'reply' && r.id === flag.postId ? {
          ...r,
          hidden: true
        } : r)
      };
    });
  }, []);
  const togglePin = React.useCallback((threadId: string) => {
    setHub(h => ({
      ...h,
      threads: h.threads.map(t => t.id === threadId ? {
        ...t,
        pinned: !t.pinned
      } : t)
    }));
  }, []);
  const hideThread = React.useCallback((threadId: string) => {
    setHub(h => ({
      ...h,
      threads: h.threads.map(t => t.id === threadId ? {
        ...t,
        hidden: true
      } : t)
    }));
  }, []);
  const sendChat = React.useCallback((body: string) => {
    const now = new Date().toISOString();
    const message: ChatMessage = {
      id: makeId('m'),
      from: 'learner',
      body: body.trim(),
      at: now
    };
    setHub(h => {
      const firstInQueue = !h.chat.some(m => m.from === 'learner');
      const ack: ChatMessage[] = firstInQueue ? [{
        id: makeId('m'),
        from: 'system',
        body: 'Your message is in the support queue. The first available team member will reply here. If nobody has picked it up within five minutes, we will also reply by email.',
        at: now
      }] : [];
      return {
        ...h,
        chat: [...h.chat, message, ...ack]
      };
    });
    void notifyTeam(`Academy support chat: ${state.learner?.email ?? 'guest'}`, {
      Form: 'Cavalla Academy support chat',
      Name: state.learner?.name ?? 'Guest',
      Email: state.learner?.email,
      Message: body.trim(),
      Page: window.location.href,
      Sent: new Date().toUTCString()
    });
  }, [state.learner]);
  const replyAsSupport = React.useCallback((body: string) => {
    setHub(h => ({
      ...h,
      chat: [...h.chat, {
        id: makeId('m'),
        from: 'support',
        body: body.trim(),
        at: new Date().toISOString()
      }]
    }));
    notify('support', 'New reply from Academy support', body.trim().slice(0, 80));
  }, [notify]);
  const leaveMessage = React.useCallback(async (email: string, body: string) => {
    await notifyTeam(`Academy support message: ${email}`, {
      Form: 'Cavalla Academy support, offline message',
      Email: email,
      Message: body.trim(),
      Page: window.location.href,
      Sent: new Date().toUTCString()
    });
    setHub(h => ({
      ...h,
      chat: [...h.chat, {
        id: makeId('m'),
        from: 'learner',
        body: body.trim(),
        at: new Date().toISOString()
      }, {
        id: makeId('m'),
        from: 'system',
        body: `Thanks. We will reply to ${email} by email.`,
        at: new Date().toISOString()
      }]
    }));
  }, []);
  const messageLearner = React.useCallback((learner: {
    email: string;
    name: string;
  }, body: string) => {
    if (!staff) return;
    const message: DirectMessage = {
      id: makeId('dm'),
      learnerEmail: learner.email,
      learnerName: learner.name,
      from: 'staff',
      senderName: staff.name,
      body: body.trim(),
      at: new Date().toISOString(),
      read: false
    };
    setHub(h => ({
      ...h,
      messages: [...h.messages, message]
    }));
    notify('message', `New message from ${staff.name}`, body.trim().slice(0, 90), '/academy/messages', {
      to: learner.email
    });
  }, [staff, notify]);
  const messageStaff = React.useCallback((body: string) => {
    const learner = state.learner;
    if (!learner) return;
    setHub(h => ({
      ...h,
      messages: [...h.messages, {
        id: makeId('dm'),
        learnerEmail: learner.email,
        learnerName: learner.name,
        from: 'learner',
        senderName: learner.name,
        body: body.trim(),
        at: new Date().toISOString(),
        read: false
      }]
    }));
    void notifyTeam(`Academy message from ${learner.email}`, {
      Form: 'Cavalla Academy learner message',
      Name: learner.name,
      Email: learner.email,
      Message: body.trim(),
      Sent: new Date().toUTCString()
    });
  }, [state.learner]);
  const markConversationRead = React.useCallback((learnerEmail: string, reader: 'staff' | 'learner') => {
    const sender = reader === 'staff' ? 'learner' : 'staff';
    setHub(h => {
      if (!h.messages.some(m => m.learnerEmail === learnerEmail && m.from === sender && !m.read)) return h;
      return {
        ...h,
        messages: h.messages.map(m => m.learnerEmail === learnerEmail && m.from === sender ? {
          ...m,
          read: true
        } : m)
      };
    });
  }, []);
  const createTeam = React.useCallback((name: string, organisation: string) => {
    if (!state.learner) return;
    const you: TeamMember = {
      name: state.learner.name,
      email: state.learner.email,
      status: 'active',
      isYou: true,
      invitedAt: new Date().toISOString()
    };
    setHub(h => ({
      ...h,
      team: {
        name: name.trim(),
        organisation: organisation.trim(),
        members: [you],
        assigned: [],
        createdAt: new Date().toISOString()
      }
    }));
  }, [state.learner]);
  const inviteMembers = React.useCallback((people: {
    name: string;
    email: string;
  }[]) => {
    let added = 0;
    setHub(h => {
      if (!h.team) return h;
      const existing = new Set(h.team.members.map(m => m.email));
      const fresh: TeamMember[] = [];
      for (const p of people) {
        const email = p.email.trim().toLowerCase();
        if (!email || existing.has(email)) continue;
        existing.add(email);
        fresh.push({
          name: p.name.trim() || email.split('@')[0],
          email,
          status: 'invited',
          invitedAt: new Date().toISOString()
        });
      }
      added = fresh.length;
      return {
        ...h,
        team: {
          ...h.team,
          members: [...h.team.members, ...fresh]
        }
      };
    });
    if (people.length) {
      void notifyTeam(`Academy team invites: ${hub.team?.name ?? 'team'}`, {
        Form: 'Cavalla Academy team invite',
        Team: hub.team?.name,
        'Invited by': state.learner?.email,
        Invitees: people.map(p => `${p.name} <${p.email}>`).join(', ')
      });
    }
    return added || people.length;
  }, [hub.team, state.learner]);
  const removeMember = React.useCallback((email: string) => {
    setHub(h => h.team ? {
      ...h,
      team: {
        ...h.team,
        members: h.team.members.filter(m => m.email !== email || m.isYou)
      }
    } : h);
  }, []);
  const assignCourses = React.useCallback((slugs: string[]) => {
    const fresh = slugs.filter(s => !hub.team?.assigned.includes(s));
    setHub(h => h.team ? {
      ...h,
      team: {
        ...h.team,
        assigned: Array.from(new Set([...h.team.assigned, ...slugs]))
      }
    } : h);
    for (const slug of fresh) {
      const course = courses.find(c => c.slug === slug);
      if (!course) continue;
      void enrol(course);
      notify('team', `${course.title} assigned to ${hub.team?.name ?? 'your team'}`, 'Every team member has been enrolled.', `/academy/courses/${slug}`);
    }
  }, [hub.team, enrol, notify, courses]);
  const visibleNotifications = React.useMemo(() => hub.notifications.filter(n => (!n.to || n.to === state.learner?.email) && (!n.courseSlug || Boolean(state.enrolments[n.courseSlug]))), [hub.notifications, state.learner, state.enrolments]);
  const markRead = React.useCallback((id: string) => {
    setHub(h => ({
      ...h,
      notifications: h.notifications.map(n => n.id === id ? {
        ...n,
        read: true
      } : n)
    }));
  }, []);
  const markAllRead = React.useCallback(() => {
    const ids = new Set(visibleNotifications.map(n => n.id));
    setHub(h => ({
      ...h,
      notifications: h.notifications.map(n => ids.has(n.id) ? {
        ...n,
        read: true
      } : n)
    }));
  }, [visibleNotifications]);
  const saveCourse = React.useCallback((course: Course, options: SaveCourseOptions = {}) => {
    const isBase = COURSES.some(c => c.slug === course.slug);
    const at = new Date().toISOString();
    setHub(h => {
      const next: HubState = isBase ? {
        ...h,
        courseOverrides: {
          ...h.courseOverrides,
          [course.slug]: course
        }
      } : {
        ...h,
        customCourses: h.customCourses.map(c => c.slug === course.slug ? course : c)
      };
      if (!options.notify) return next;
      const version = (h.courseUpdates[course.slug]?.version ?? 1) + 1;
      const note = options.note?.trim() || 'New and revised lesson content.';
      return {
        ...next,
        courseUpdates: {
          ...h.courseUpdates,
          [course.slug]: {
            version,
            note,
            at,
            by: staff?.name ?? 'Cavalla Academy'
          }
        },
        notifications: [{
          id: makeId('n'),
          type: 'course_update' as const,
          title: `${course.title} has been updated`,
          body: `${note} Your progress is preserved.`,
          link: `/academy/courses/${course.slug}`,
          read: false,
          at,
          courseSlug: course.slug
        }, ...h.notifications].slice(0, 80)
      };
    });
    if (!options.notify) return 0;
    const roster = SAMPLE_ROSTER.filter(l => course.slug in l.enrolled && l.email !== state.learner?.email).length;
    return roster + (state.enrolments[course.slug] ? 1 : 0);
  }, [staff, state.learner, state.enrolments]);
  const createCourse = React.useCallback((draft: Omit<Course, 'slug' | 'number'>) => {
    const base = slugify(draft.title) || 'new-course';
    let slug = base;
    for (let i = 2; courses.some(c => c.slug === slug); i++) slug = `${base}-${i}`;
    const number = Math.max(...courses.map(c => c.number)) + 1;
    setHub(h => ({
      ...h,
      customCourses: [...h.customCourses, {
        ...draft,
        slug,
        number,
        custom: true
      }],
      unpublished: [...h.unpublished, slug]
    }));
    return slug;
  }, [courses]);
  const setPublished = React.useCallback((slug: string, published: boolean) => {
    setHub(h => ({
      ...h,
      unpublished: published ? h.unpublished.filter(s => s !== slug) : Array.from(new Set([...h.unpublished, slug]))
    }));
  }, []);
  const setRole = React.useCallback((email: string, next: AcademyRole) => {
    setHub(h => ({
      ...h,
      roleOverrides: {
        ...h.roleOverrides,
        [email]: next
      }
    }));
  }, []);
  const value: HubValue = {
    hub,
    role,
    staff,
    author,
    courses,
    publishedCourses,
    findCourse,
    isPublished,
    channels,
    staffSignIn,
    staffSignOut,
    setInterests,
    finishOnboarding,
    setEmailPref,
    createThread,
    addReply,
    toggleUpvote,
    flagPost,
    moderate,
    togglePin,
    hideThread,
    sendChat,
    replyAsSupport,
    leaveMessage,
    messageLearner,
    messageStaff,
    markConversationRead,
    createTeam,
    inviteMembers,
    removeMember,
    assignCourses,
    visibleNotifications,
    markRead,
    markAllRead,
    saveCourse,
    createCourse,
    setPublished,
    setRole
  };
  return <HubContext.Provider value={value} data-id="element-1436" data-mp="1wjsvjhbmfllf">{children}</HubContext.Provider>;
}
export function useAcademyHub(): HubValue {
  const value = React.useContext(HubContext);
  if (!value) throw new Error('useAcademyHub must be used inside AcademyHubProvider');
  return value;
}
export function canModerate(role: AcademyRole): boolean {
  return role === 'moderator' || role === 'admin';
}

/** Instructors and admins can change course content. Only admins build new courses or publish. */
export function canEditCourses(role: AcademyRole): boolean {
  return role === 'instructor' || role === 'admin';
}
function readHub(): HubState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? {
      ...EMPTY,
      ...(JSON.parse(raw) as Partial<HubState>)
    } : EMPTY;
  } catch {
    return EMPTY;
  }
}
function readStaff(): StaffSession | null {
  try {
    const raw = window.localStorage.getItem(STAFF_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as Partial<StaffSession>;
    if (!saved.email || !saved.name || !saved.role || !(saved.role in ROLE_LABELS)) return null;
    return saved as StaffSession;
  } catch {
    return null;
  }
}
function slugify(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
}
function makeId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}